import type { Agreement, LinkState, OrgStatus } from './rules.ts';
import { assess, inOrg } from './threats.ts';

// The rules of syncing with RSI (plan 5B, R2 to R4), as pure functions: what a scrape does
// to a profile, what a roster pass does to everyone, when a handle has moved, and which
// snapshots to keep. The server reads and writes; these decide.

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;
/** `members_profiles.bio` holds this much. */
export const MAX_BIO = 10_000;
/** A roster pass rewrites `roster_seen_at` on an unchanged row only when it is this old. */
export const SEEN_REFRESH_MS = DAY_MS;
/** ITCBot's handle rule, as `packages/rsi` has it: a handle is put in a URL, so nothing else is accepted. */
export const HANDLE = /^[A-Za-z0-9_-]{3,30}$/;

/** PocketBase's date format, which `toPbDate` in core makes too. */
const stamp = (at: Date): string => at.toISOString().replace('T', ' ');

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

// --- Bio codes (R4) ---------------------------------------------------------------------

/** ITCBot's pattern: eight upper-case hex digits. */
const BIOCODE = /\[IDN:([0-9A-F]{8})\]/;

/** The first `[IDN:XXXXXXXX]` in a bio, or null. */
export function findBiocode(bio: string): string | null {
  const match = BIOCODE.exec(bio);
  return match === null ? null : `[IDN:${match[1] ?? ''}]`;
}

// --- Applying a public scrape (R2) --------------------------------------------------------

/** An org as a profile holds it. */
export interface OrgView {
  symbol: string;
  name: string;
  activities: string[];
}

/** What R2 reads of a profile. */
export interface ScrapeTarget {
  bio: string;
  orgStatus: OrgStatus;
  visibility: string;
  currentOrgs: readonly OrgView[];
  orgHistory: readonly string[];
  redactedOrgCount: number;
  isThreat: boolean;
  threatReasons: readonly string[];
  hostileOrgs: readonly string[];
}

/** What `packages/rsi` reads of one person's public pages, when it worked. */
export interface Scrape {
  bio: string;
  /** False when the organizations page failed: the org fields then mean nothing. */
  orgsRead: boolean;
  redactedOrgCount: number;
  affiliations: readonly { name: string; symbol: string; activities: readonly string[] }[];
}

export interface ScrapeRules {
  hostileOrgs: readonly string[];
  threatAffiliations: readonly string[];
}

/** The fields R2 may change, and only those that did. `permanent_threat` and `ban_reason` are never in it. */
export interface ScrapePatch {
  bio?: string;
  current_orgs?: OrgView[];
  redacted_org_count?: number;
  org_history?: string[];
  is_threat?: boolean;
  threat_reasons?: string[];
  hostile_orgs?: string[];
}

/**
 * R2. A scrape that failed never gets here (nothing changes). With `orgsRead` false only
 * the bio updates. Otherwise the orgs update and every one seen joins the history, which
 * only grows. Reasons found replace the stored ones and set `is_threat`; none found
 * keeps both, so a clean scrape never clears a judgment. Only a pardon does.
 */
export function applyScrape(target: ScrapeTarget, scrape: Scrape, rules: ScrapeRules): ScrapePatch {
  const patch: ScrapePatch = {};
  const bio = scrape.bio.slice(0, MAX_BIO);
  if (bio !== target.bio) patch.bio = bio;
  if (!scrape.orgsRead) return patch;

  const orgs: OrgView[] = scrape.affiliations.map((org) => ({ symbol: org.symbol.toUpperCase(), name: org.name, activities: [...org.activities] }));
  if (!same(orgs, target.currentOrgs)) patch.current_orgs = orgs;
  if (scrape.redactedOrgCount !== target.redactedOrgCount) patch.redacted_org_count = scrape.redactedOrgCount;

  const known = new Set(target.orgHistory.map((symbol) => symbol.toUpperCase()));
  const added = [...new Set(orgs.map((org) => org.symbol).filter((symbol) => symbol !== '' && !known.has(symbol)))].sort();
  const history = [...target.orgHistory, ...added];
  if (added.length > 0) patch.org_history = history;

  const found = assess({
    profile: { orgs, redactedOrgCount: scrape.redactedOrgCount },
    history,
    hostileOrgs: rules.hostileOrgs,
    threatAffiliations: rules.threatAffiliations,
    isMember: inOrg(target.orgStatus),
    visibility: target.visibility,
  });
  if (found.reasons.length > 0) {
    if (!same(found.reasons, target.threatReasons)) patch.threat_reasons = found.reasons;
    if (!same(found.hostileOrgs, target.hostileOrgs)) patch.hostile_orgs = found.hostileOrgs;
    if (!target.isThreat) patch.is_threat = true;
  }
  return patch;
}

// --- What changed for a role slot or for R6 ---------------------------------------------

/** What `isVerified` and the role slots read of a profile. */
export interface StatusView {
  discordId: string;
  linkState: LinkState;
  orgStatus: OrgStatus;
  rank: string;
  roles: readonly string[];
  isThreat: boolean;
  permanentThreat: boolean;
  agreement: Agreement;
}

/** Whether `members.status_changed` is due: only for someone with a Discord id, as no one else has roles to fix. */
export function statusChanged(before: StatusView, after: StatusView): boolean {
  if (after.discordId === '') return false;
  return (
    before.linkState !== after.linkState ||
    before.orgStatus !== after.orgStatus ||
    before.rank !== after.rank ||
    !same(before.roles, after.roles) ||
    before.isThreat !== after.isThreat ||
    before.permanentThreat !== after.permanentThreat ||
    before.agreement !== after.agreement
  );
}

/** Whether `members.joined_org` is due: they weren't in the org (or weren't there at all) and now are. A change from member to affiliate is not a join. */
export function joinedOrg(before: OrgStatus | null, after: OrgStatus): boolean {
  return inOrg(after) && (before === null || !inOrg(before));
}

// --- A roster pass (R3) -----------------------------------------------------------------

/** One row of the org's roster, as `packages/rsi` reads it. */
export interface RosterRow {
  handle: string;
  visibility: string;
  memberType: 'Member' | 'Affiliate';
  rank: string;
  roles: readonly string[];
}

/** What R3 reads of a stored profile. */
export interface RosterTarget {
  orgStatus: OrgStatus;
  rank: string;
  roles: readonly string[];
  visibility: string;
  rosterSeenAt: Date | null;
  leftOrgAt: Date | null;
}

export interface RosterPatch {
  org_status?: OrgStatus;
  rsi_rank?: string;
  rsi_roles?: string[];
  rsi_visibility?: string;
  roster_seen_at?: string;
  left_org_at?: '';
}

/** What a roster row says a person's `org_status` is. */
export const rosterStatus = (row: RosterRow): 'member' | 'affiliate' => (row.memberType === 'Member' ? 'member' : 'affiliate');

/**
 * R3, for a handle with a profile: type, roles, rank and visibility update, `org_status`
 * is set, and `roster_seen_at` is set. A row that changes nothing isn't written, so
 * `roster_seen_at` is renewed on one only once it is a day old.
 */
export function rosterPatch(target: RosterTarget, row: RosterRow, now: Date): RosterPatch {
  const patch: RosterPatch = {};
  const status = rosterStatus(row);
  if (target.orgStatus !== status) patch.org_status = status;
  if (target.rank !== row.rank) patch.rsi_rank = row.rank;
  if (!same(target.roles, row.roles)) patch.rsi_roles = [...row.roles];
  if (target.visibility !== row.visibility) patch.rsi_visibility = row.visibility;
  if (target.leftOrgAt !== null) patch.left_org_at = '';
  const stale = target.rosterSeenAt === null || now.getTime() - target.rosterSeenAt.getTime() >= SEEN_REFRESH_MS;
  if (stale || Object.keys(patch).length > 0) patch.roster_seen_at = stamp(now);
  return patch;
}

/** R3, for an in-org profile not in the pass. */
export function leavePatch(now: Date): { org_status: 'none'; left_org_at: string } {
  return { org_status: 'none', left_org_at: stamp(now) };
}

/** What R3 reads of every stored profile to plan a pass. */
export interface StoredRoster extends RosterTarget {
  id: string;
  /** The handle lower-cased: how a roster row finds its profile. */
  handleKey: string;
}

export type RosterPlan =
  | { ok: false; reason: string; rows: number; inOrg: number; minimum: number }
  | { ok: true; create: RosterRow[]; update: { id: string; row: RosterRow }[]; leave: string[]; unchanged: number; skipped: number };

/** Each handle once, the first row winning, as a handle is case-blind. */
export function uniqueRows(rows: readonly RosterRow[]): RosterRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = row.handle.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * R3. Only a finished pass can mark anyone as left, and only if it holds at least
 * `(100 − maxDropPercent)%` of those stored as in the org: a short list, whatever the
 * cause, changes nothing. Past that, a handle with no profile is created, one with a
 * profile is updated if it changed, and an in-org profile not in the pass leaves.
 */
export function planRoster(stored: readonly StoredRoster[], pass: readonly RosterRow[], options: { maxDropPercent: number; now: Date }): RosterPlan {
  const rows = uniqueRows(pass);
  const present = stored.filter((profile) => inOrg(profile.orgStatus));
  const minimum = Math.ceil((present.length * (100 - options.maxDropPercent)) / 100);
  if (rows.length < minimum) {
    return {
      ok: false,
      reason: `The roster showed ${String(rows.length)} people, and ${String(present.length)} are stored as in the org. A pass must show at least ${String(minimum)} (the setting allows a drop of ${String(options.maxDropPercent)}%), so nothing was changed.`,
      rows: rows.length,
      inOrg: present.length,
      minimum,
    };
  }
  const byKey = new Map(stored.map((profile) => [profile.handleKey, profile]));
  const create: RosterRow[] = [];
  const update: { id: string; row: RosterRow }[] = [];
  let unchanged = 0;
  let skipped = 0;
  for (const row of rows) {
    const profile = byKey.get(row.handle.toLowerCase());
    if (profile === undefined) {
      if (HANDLE.test(row.handle)) create.push(row);
      else skipped += 1;
    } else if (Object.keys(rosterPatch(profile, row, options.now)).length > 0) update.push({ id: profile.id, row });
    else unchanged += 1;
  }
  const seen = new Set(rows.map((row) => row.handle.toLowerCase()));
  const leave = present.filter((profile) => !seen.has(profile.handleKey)).map((profile) => profile.id);
  return { ok: true, create, update, leave, unchanged, skipped };
}

// --- Passes ----------------------------------------------------------------------------

/** Whether `hours` have passed since a pass began. A pass that never ran is due. */
export function passDue(startedAt: Date | null, hours: number, now: Date): boolean {
  return startedAt === null || now.getTime() - startedAt.getTime() >= hours * HOUR_MS;
}

// --- Snapshots ---------------------------------------------------------------------------

/** The ISO year and week of a date in UTC, as Python's `isocalendar()` gives them. */
function isoWeek(date: Date): string {
  const thursday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  thursday.setUTCDate(thursday.getUTCDate() + 4 - (thursday.getUTCDay() || 7));
  const year = thursday.getUTCFullYear();
  return `${String(year)}-${String(Math.ceil(((thursday.getTime() - Date.UTC(year, 0, 1)) / DAY_MS + 1) / 7))}`;
}

/**
 * ITCBot's thinning of `sync_history.json`: everything under 8 days old stays; to 35 days,
 * the first of each ISO week; beyond that the first of each month. Returns the ids to
 * delete. Age is whole days, as Python's `timedelta.days` counts them.
 */
export function thinSnapshots(snapshots: readonly { id: string; at: Date }[], now: Date): string[] {
  const ordered = [...snapshots].sort((a, b) => a.at.getTime() - b.at.getTime() || (a.id < b.id ? -1 : 1));
  const weeks = new Set<string>();
  const months = new Set<string>();
  const drop: string[] = [];
  for (const { id, at } of ordered) {
    const days = Math.floor((now.getTime() - at.getTime()) / DAY_MS);
    if (days <= 7) continue;
    const bucket = days <= 35 ? weeks : months;
    const key = days <= 35 ? isoWeek(at) : `${String(at.getUTCFullYear())}-${String(at.getUTCMonth() + 1)}`;
    if (bucket.has(key)) drop.push(id);
    else bucket.add(key);
  }
  return drop;
}
